import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import './Testimonials.css';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      quote:
        'I needed a refill and could not leave work. The telehealth visit was quick, clear, and I knew the fee up front.',
      company: 'Medication refill visit',
    },
    {
      quote:
        'Finally a way to talk through my labs without feeling rushed. I understood my numbers and what to do next.',
      company: 'Lab review visit',
    },
    {
      quote:
        'Managing blood pressure is easier when someone checks in and explains things in plain language. The follow-up rhythm works for me.',
      company: 'Chronic condition support',
    },
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => (prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1));
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1));
  };

  const goToTestimonial = (index) => {
    setCurrentIndex(index);
  };

  return (
    <section className="testimonials" id="testimonials">
      <div className="container">
        <motion.div
          className="testimonials-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title text-center">What people value about telehealth</h2>
          <div className="testimonials-subtitle text-center">
            <Quote className="quote-icon" />
            <span>Convenience, clarity, and care when it is appropriate—not for emergencies</span>
          </div>
        </motion.div>

        <div className="testimonials-content">
          <motion.button
            type="button"
            className="nav-button nav-prev"
            onClick={prevTestimonial}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Previous testimonial"
          >
            <ChevronLeft size={24} />
          </motion.button>

          <div className="testimonial-display">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                className="testimonial-card"
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
              >
                <div className="testimonial-quote">
                  <Quote className="quote-mark" />
                  <blockquote>{testimonials[currentIndex].quote}</blockquote>
                </div>

                <div className="testimonial-author">
                  <div className="author-info">
                    <p className="author-company">{testimonials[currentIndex].company}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <motion.button
            type="button"
            className="nav-button nav-next"
            onClick={nextTestimonial}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Next testimonial"
          >
            <ChevronRight size={24} />
          </motion.button>
        </div>

        <div className="testimonial-indicators">
          {testimonials.map((_, index) => (
            <motion.button
              key={index}
              type="button"
              className={`indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToTestimonial(index)}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.8 }}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>

        <motion.div
          className="testimonials-cta"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <h3>Ready for healthcare on your terms?</h3>
          <p>See our services and transparent fees, then reach out by call, email, or text.</p>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/contact" className="btn btn-primary">
              Contact us
            </Link>
          </motion.div>
        </motion.div>
      </div>

      <div className="testimonials-bg">
        <motion.div
          className="bg-circle bg-circle-1"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="bg-circle bg-circle-2"
          animate={{
            scale: [1.1, 1, 1.1],
            opacity: [0.4, 0.7, 0.4],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
      </div>
    </section>
  );
};

export default Testimonials;
